/**
 * Media API Client
 * Focused client for video gallery functionality
 */

import { createLogger } from '@shared/helpers/logger.ts';
import { z } from 'zod';

const log = createLogger({ module: 'api' });

// Base URL for API calls (proxied through vite in dev, direct in prod)
const API_BASE = '/api/media';

// === Zod Schemas (simplified from generated) ===

export const VideoSearchParamsSchema = z.object({
  'session-id': z.string().optional(),
  'with-storage': z.boolean().optional(),
  'with-timestamp': z.boolean().optional(),
  'with-coordinates': z.boolean().optional(),
  'sort-order': z.enum(['asc', 'desc']).optional(),
  'source-type': z.enum(['day', 'heat']).optional(),
  'storage-path-filter': z.string().optional(),
  offset: z.number().optional(),
  limit: z.number().optional(),
});

export const VideoItemSchema = z.object({
  id: z.string(),
  timestamp: z.number().optional(),
  'storage-path': z.string().optional(),
  name: z.string().optional(),
  // Position fields (when with-coordinates=true)
  longitude: z.number().optional(),
  latitude: z.number().optional(),
  altitude: z.number().optional(),
});

export const VideoSearchResponseSchema = z.object({
  total: z.number(),
  items: z.array(VideoItemSchema),
});

// Full video data schema (from /videos/data/{id})
export const VideoFullDataSchema = z.object({
  id: z.string(),
  uuid: z.string().optional(),
  timestamp: z.number().optional(),
  'storage-path': z.string().optional(),
  'session-id': z.number().optional(),
  'zone-id': z.number().optional(),
  longitude: z.number().optional(),
  latitude: z.number().optional(),
  altitude: z.number().optional(),
  azimuth: z.number().optional(),
  elevation: z.number().optional(),
  bank: z.number().optional(),
  'fix-type': z.string().optional(),
  'target-type': z.string().optional(),
  important: z.boolean().optional(),
  deleted: z.boolean().optional(),
  'deleted-on': z.number().optional(),
  location: z.string().optional(),
  name: z.string().optional(),
});

export const VideoDataResponseSchema = z.object({
  total: z.number(),
  items: z.array(VideoFullDataSchema),
});

export const SessionDataSchema = z.object({
  'session-id': z.number(),
  counts: z.object({
    videos: z.number(),
    photos: z.number(),
    targets: z.number(),
  }),
  'first-timestamp': z.number().optional(),
  'last-timestamp': z.number().optional(),
  deleted: z.boolean(),
  name: z.string().optional(),
});

// === Types ===

export type VideoSearchParams = z.infer<typeof VideoSearchParamsSchema>;
export type VideoItem = z.infer<typeof VideoItemSchema>;
export type VideoSearchResponse = z.infer<typeof VideoSearchResponseSchema>;
export type VideoFullData = z.infer<typeof VideoFullDataSchema>;
export type VideoDataResponse = z.infer<typeof VideoDataResponseSchema>;
export type SessionData = z.infer<typeof SessionDataSchema>;

// === Helper: Build thumbnail URL from storage path ===

export function buildThumbnailUrl(storagePath: string): string {
  // /mnt/rec/video/general/{session}/{day|heat}/{YYYY/MM/DD/HH}/{frag}/{uuid}
  // → /rec/video/general/...

  // Validate path format
  const isValid =
    /\/mnt\/rec\/video\/(general|important)\/[\w]+\/(day|heat)\/\d{4}\/\d{2}\/\d{2}\/\d{2}\/\d+\/[a-f0-9-]+/i.test(
      storagePath,
    );

  if (!isValid) {
    throw new Error(`Invalid storage path format: ${storagePath}`);
  }

  // Convert to web-accessible path, prepend API base for cross-origin access
  const apiBase = ((globalThis as Record<string, unknown>).__SYCH_API_BASE__ as string) || '';
  return `${apiBase}${storagePath.replace('/mnt/rec/', '/rec/')}`;
}

export function buildVideoUrl(storagePath: string): string {
  const apiBase = ((globalThis as Record<string, unknown>).__SYCH_API_BASE__ as string) || '';
  return `${apiBase}${storagePath.replace('/mnt/rec/', '/rec/')}`;
}

// === API Functions ===

export async function searchVideos(params: VideoSearchParams): Promise<VideoSearchResponse> {
  const searchParams = new URLSearchParams();

  if (params['session-id']) searchParams.set('session-id', params['session-id']);
  if (params['with-storage']) searchParams.set('with-storage', 'true');
  if (params['with-timestamp']) searchParams.set('with-timestamp', 'true');
  if (params['with-coordinates']) searchParams.set('with-coordinates', 'true');
  if (params['sort-order']) searchParams.set('sort-order', params['sort-order']);
  if (params['source-type']) searchParams.set('source-type', params['source-type']);
  if (params['storage-path-filter'])
    searchParams.set('storage-path-filter', params['storage-path-filter']);
  if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));

  const url = `${API_BASE}/videos/search?${searchParams}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return VideoSearchResponseSchema.parse(data);
}

// Fetch full video data including azimuth, elevation, etc.
export async function getVideoData(videoId: string): Promise<VideoFullData> {
  const url = `${API_BASE}/videos/data/${encodeURIComponent(videoId)}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch video data: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const parsed = VideoDataResponseSchema.parse(data);
  const item = parsed.items[0];

  if (!item) {
    throw new Error(`No data found for video ID: ${videoId}`);
  }

  return item;
}

export async function getSessionData(sessionId: number): Promise<SessionData> {
  const url = `${API_BASE}/sessions/data/${sessionId}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return SessionDataSchema.parse(data);
}

// === Fixed filenames - no directory listing needed ===

export function getThumbnailFilename(): string {
  return 'osd_thumbnail.jpg';
}

export function getFullPngFilename(): string {
  return 'osd.jpg';
}

export function getPreviewFilename(): string {
  return 'preview.mp4';
}

export function getHiResFilename(): string {
  return 'video.mp4';
}

// === Day Video Filter ===

export function isDayVideo(item: VideoItem): boolean {
  return item['storage-path']?.includes('/day/') ?? false;
}

export function filterDayVideos(items: VideoItem[]): VideoItem[] {
  return items.filter(isDayVideo);
}

// === Video URL Construction ===

/**
 * Build hi-res video URL from storage path.
 * Uses fixed filename: video.mp4
 *
 * Path format: /mnt/rec/video/general/0/day/2025/12/18/12/0169/UUID
 * Filename: video.mp4
 */
export function buildHiResVideoUrl(storagePath: string): string | null {
  if (!storagePath) return null;

  const dirUrl = buildThumbnailUrl(storagePath);
  const url = `${dirUrl}/video.mp4`;
  log.debug(`buildHiResVideoUrl: path=${storagePath}, url=${url}`);
  return url;
}

/**
 * Build quality-aware video URL from storage path.
 *
 * @param storagePath - Storage path like /mnt/rec/video/general/0/day/...
 * @param quality - 'full' for video.mp4 (1920x1080), 'mini' for preview.mp4 (480x270)
 * @returns Web-accessible video URL or null if storagePath is invalid
 */
export function buildQualityVideoUrl(storagePath: string, quality: 'full' | 'mini'): string | null {
  if (!storagePath) return null;

  const dirUrl = buildThumbnailUrl(storagePath);
  const filename = quality === 'full' ? 'video.mp4' : 'preview.mp4';
  const url = `${dirUrl}/${filename}`;
  log.debug(`buildQualityVideoUrl: quality=${quality}, path=${storagePath}, url=${url}`);
  return url;
}
